import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { formatDate } from '../../../../config/appConfig';
import { useCustomFieldValues } from '../../../custom-fields/useData';
import { CUSTOM_FIELD_ENTITY } from '../../../custom-fields/entityTypes';
import type { CustomField } from '../../../custom-fields/useData';

interface ClientCustomFieldsPanelProps {
  clientId: number;
}

function formatFieldValue(field: CustomField): string {
  const value = field.value;
  if (value === undefined || value === null || value === '') return '—';
  if (field.fieldType === 'checkbox') return value ? 'Yes' : 'No';
  if (field.fieldType === 'date') {
    try {
      return formatDate(String(value));
    } catch {
      return String(value);
    }
  }
  if (field.fieldType === 'select' && field.options) {
    const match = (field.options as Array<{ value?: string; label?: string }>).find(
      (o) => String(o.value) === String(value),
    );
    return match?.label ?? String(value);
  }
  return String(value);
}

const ClientCustomFieldsPanel: React.FC<ClientCustomFieldsPanelProps> = ({ clientId }) => {
  const { colors } = useTheme();
  const { data: fields, isLoading } = useCustomFieldValues(CUSTOM_FIELD_ENTITY.CLIENT, clientId);

  if (isLoading) {
    return (
      <div className={cn('rounded-xl border p-4', colors.border, colors.background)}>
        <p className={cn('text-sm', colors.textSecondary)}>Loading custom fields…</p>
      </div>
    );
  }

  const withValues = (fields ?? []).filter(
    (f) => f.value !== undefined && f.value !== null && f.value !== '',
  );

  if (withValues.length === 0) return null;

  return (
    <div className={cn('rounded-xl border overflow-hidden', colors.border, colors.background)}>
      <div className={cn('flex items-center justify-between gap-3 px-4 py-3 border-b', colors.border, colors.surfaceHover)}>
        <h3 className={cn('text-sm font-semibold', colors.text)}>Custom fields</h3>
        <Link
          to={`/clients/${clientId}/edit`}
          className={cn('text-xs font-medium hover:underline', colors.textSecondary)}
        >
          Edit →
        </Link>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 p-4">
        {withValues.map((field) => (
          <div key={field.id} className="min-w-0">
            <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>
              {field.fieldLabel}
            </dt>
            <dd className={cn('text-sm font-medium mt-0.5 break-words', colors.text)}>
              {formatFieldValue(field)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export default ClientCustomFieldsPanel;
